import { useFlyOutProductsBasedOnSelectedCloud } from '../../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useCallback, useMemo, useState } from 'react'
import {
  ButtonV2,
  Filters,
  FiltersApplied,
  LabraToolTipV2,
  RadioButtonsV2,
  SectionProps,
  SearchBoxComponent,
  useAuth,
} from '@labrav/react-components'
import { Link } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import {
  actionTypeWrapper,
  extractAccountIdFromCombinedId,
  getLabelFromMapping,
  getSelectedAccountIdWithCloud,
  isLoading,
  LoadingTypes,
  providerToAuthCloud,
  setSelectedAccount,
  startLoading,
  stopLoading,
  useAccountId,
  useCloudAccounts,
  useCurrentCloudProvider,
  CloudType,
  CloudAccountChangeParams,
  CloudAccountChangeV2,
} from '@labrav/common'
import { useFlag } from '@labrav/flags'

import { useStyles } from './SubscriptionFilterSection.styles'
import {
  capitalizeFirstLetter,
  labraPermissionsHelpUrl,
} from '../../../../utils/subscriptionsListHelpers'
import {
  isExpiringSoonSubscriptionStatusList,
  subscriptionsRangeStatus,
  subscriptionFilterCriteriaOptions,
  subscriptionFilterCriteriaOptionsGcp,
  type SubscriptionFilter,
} from '../../../../utils/subscriptionsFilterMapping'
import { ProductSelectorDropdown } from '../../../../common/components/ProductSelectorDropdown/ProductSelectorDropdown'
import {
  FlyoutFunction,
  Product,
  ProductStatus,
} from '../../../../modules/productsListing/types'
import { useDispatch, useSelector } from '../../../../store'
import type { RootState } from '../../../../store/types'
import { clearSubscriptions } from '../../../../modules/subscription/action'
import { SubscriptionSelectionModal } from '../../SubscriptionSelectionModal/SubscriptionSelectionModal'
import { getActiveReceivedResaleAuthorizations } from '../../../../modules/subscription/subscriptionsListDeps'
import {
  clearProductsListing,
  setSelectedProductOnListing,
} from '../../../../modules/productsListing/action'
import { setCloudMarketplaceProductListing } from '../../../../modules/productListingFilter/action'
import { SubscriptionsState } from '../../../../modules/subscription/reducer'
import { ChannelOfferCreationModal } from '../../../ChannelOffers/ChannelOfferCreationModal'

interface SubscriptionFilterSectionProp {
  options: string[]
  handleSearchClick: () => void
  loading: boolean
  searchNow: boolean
  setSearchNow: React.Dispatch<React.SetStateAction<boolean>>
  showCrossMark: boolean
  setShowCrossMark: React.Dispatch<React.SetStateAction<boolean>>
  inputValue: string
  setInputValue: React.Dispatch<React.SetStateAction<string>>
  selectedOption: string
  setSelectedOption: React.Dispatch<React.SetStateAction<string>>
  selectedSubscriptionsFilter: subscriptionsRangeStatus | ''
  setSelectedSubscriptionsFilter: React.Dispatch<
    React.SetStateAction<subscriptionsRangeStatus | ''>
  >
  appliedFilteredValues: SubscriptionFilter
  setAppliedFilteredValues: React.Dispatch<
    React.SetStateAction<SubscriptionFilter>
  >
}

export const SubscriptionFilterSection: React.FC<
  SubscriptionFilterSectionProp
> = ({
  options,
  handleSearchClick,
  loading,
  searchNow,
  setSearchNow,
  showCrossMark,
  setShowCrossMark,
  inputValue,
  setInputValue,
  selectedOption,
  setSelectedOption,
  selectedSubscriptionsFilter,
  setSelectedSubscriptionsFilter,
  appliedFilteredValues,
  setAppliedFilteredValues,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { flag: contractStatusUpdate } = useFlag('contractStatusUpdate')
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || fallbackAccountId
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const marketplaceCloudAccounts = useCloudAccounts('flyout')
  const filteredCloudAccounts = useMemo(
    () => marketplaceCloudAccounts,
    [marketplaceCloudAccounts]
  )
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )
  const { subscriptions } = useSelector<SubscriptionsState>(
    state => state?.subscriptions || { subscriptions: [] }
  )
  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    (state: RootState) =>
      (
        state as RootState & {
          receivedResellerAuthorization?: {
            selectedActiveReceivedResellerAuthorization?: string
          }
        }
      ).receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization || ''
  )
  const loadingActiveResellerAuth = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION)
  )
  const { products, selectedProduct: productId } =
    useFlyOutProductsBasedOnSelectedCloud()
  const selectedProductDetails = useMemo(() => {
    return (
      products.find(product => product.productId === productId) ||
      ({} as Product)
    )
  }, [products, productId])

  const { loading: authLoading, canDo } = useAuth()
  const canCreateABO = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(selectedCloudTypeProductListing),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, selectedCloudTypeProductListing])
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState('')
  const [cppoSubscription, setCppoSubscription] = useState('')

  const handleClick = () => setIsModalOpen(true)
  const handleModalClose = () => setIsModalOpen(false)

  const acceptSelectedSubscription = async (subscriptionId: string) => {
    const selectedSubscriptionItem = (subscriptions || []).find(
      subscriptionItem => subscriptionItem?.subscriptionId === subscriptionId
    )
    if (selectedSubscriptionItem?.offerType === 'CPPO') {
      await dispatch(
        getActiveReceivedResaleAuthorizations({
          partnerId: accountId,
          subscriptionId,
        })
      )
      setSelectedSubscription(subscriptionId)
      setCppoSubscription('create')
    } else {
      setIsModalOpen(false)
      setSelectedSubscription(subscriptionId)
      navigate(
        `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionId}/edit`,
        {
          state: { subscriptionId },
        }
      )
    }
  }
  const handleNext = (subscriptionId?: string) => {
    navigate(
      `/flyout/${routeCloud}/${accountId}/subscriptions/${subscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`
    )
  }

  const handleSubscriptionStatusChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedSubscriptionsFilter(e.target.value as subscriptionsRangeStatus)
  }

  const subscriptionFilterCriteriaOptionsForCloud = useMemo(
    () =>
      selectedCloudTypeProductListing === 'GCP'
        ? subscriptionFilterCriteriaOptionsGcp
        : subscriptionFilterCriteriaOptions,
    [selectedCloudTypeProductListing]
  )

  const subscriptionStatusLabel = useMemo(
    () =>
      getLabelFromMapping(
        subscriptionFilterCriteriaOptionsForCloud,
        selectedSubscriptionsFilter
      ),
    [selectedSubscriptionsFilter, subscriptionFilterCriteriaOptionsForCloud]
  )

  const sections: SectionProps[] = [
    {
      title: 'Status',
      type: 'radio',
      content: (
        <div data-testid="expiry-date-radio" className={classes.filterSection}>
          <RadioButtonsV2
            checkedValue={{
              key: selectedSubscriptionsFilter || '',
              value: selectedSubscriptionsFilter || '',
            }}
            name="subscriptionStatus"
            radioButtonMappings={subscriptionFilterCriteriaOptionsForCloud}
            handleChange={handleSubscriptionStatusChange}
            vertical={true}
            gapBetween={'0'}
          />
        </div>
      ),
      selectedValues: subscriptionStatusLabel || undefined,
      onDelete: () => setSelectedSubscriptionsFilter(''),
    },
  ]

  const applyFilters = useCallback(async () => {
    dispatch(clearSubscriptions())
    setAppliedFilteredValues({
      status: selectedSubscriptionsFilter,
      ...(selectedSubscriptionsFilter &&
      isExpiringSoonSubscriptionStatusList.includes(selectedSubscriptionsFilter)
        ? {
            expiringDays: selectedSubscriptionsFilter,
            status: subscriptionsRangeStatus.ACTIVE,
          }
        : {}),
    })
  }, [dispatch, selectedSubscriptionsFilter, setAppliedFilteredValues])

  const onDeleteFiltersAppliedChip = useCallback(async () => {
    dispatch(clearSubscriptions())
    setSelectedSubscriptionsFilter(
      contractStatusUpdate?.value
        ? subscriptionsRangeStatus.ALL
        : subscriptionsRangeStatus.ACTIVE
    )
    setAppliedFilteredValues({ status: '' })
  }, [
    dispatch,
    contractStatusUpdate?.value,
    setAppliedFilteredValues,
    setSelectedSubscriptionsFilter,
  ])

  const selectedFilter = appliedFilteredValues.status
    ? [
        appliedFilteredValues.expiringDays
          ? selectedCloudTypeProductListing === 'GCP'
            ? `Ending in ${appliedFilteredValues.expiringDays} days`
            : `Expiring in ${appliedFilteredValues.expiringDays} days`
          : getLabelFromMapping(
              subscriptionFilterCriteriaOptionsForCloud,
              appliedFilteredValues.status
            ) || capitalizeFirstLetter(appliedFilteredValues.status),
      ]
    : []

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const nextAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params
      const cloudPath = cloud.toLowerCase()

      await dispatch(startLoading(LoadingTypes.GENERAL))
      await dispatch(
        setSelectedAccount(nextAccountId, 'flyout', cloud as CloudType)
      )
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))

      navigate(`/flyout/${cloudPath}/${nextAccountId}/subscriptions`, {
        replace: true,
      })
      setInputValue('')
      setShowCrossMark(false)
      setSearchNow(false)
    },
    [
      dispatch,
      navigate,
      selectedCloudTypeProductListing,
      setInputValue,
      setSearchNow,
      setShowCrossMark,
    ]
  )

  const createButton = (
    <LabraToolTipV2
      tooltipPlacement="bottom"
      description={
        !canCreateABO ? (
          <>
            This action is disabled because you have read-only access. To check
            all permissions click{' '}
            <Link
              href={labraPermissionsHelpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.permissionLink}
            >
              here.
            </Link>
          </>
        ) : selectedProductDetails.status ===
          ProductStatus.PRODUCT_STATUS_RESTRICTED ? (
          'Cannot create ABO for restricted products.'
        ) : (
          'Create agreement based offer'
        )
      }
    >
      <ButtonV2
        data-testid="renew-subscription-button"
        className={classes.renewButton}
        onClick={handleClick}
        disabled={
          selectedProductDetails?.status ===
            ProductStatus.PRODUCT_STATUS_RESTRICTED || !canCreateABO
        }
        title={
          !canCreateABO
            ? 'This action is disabled because you have read-only access.'
            : undefined
        }
      >
        Create ABO
      </ButtonV2>
    </LabraToolTipV2>
  )

  const showRevampSearchBox =
    selectedCloudTypeProductListing === 'AWS' ||
    selectedCloudTypeProductListing === 'GCP'

  return (
    <>
      <div className={classes.root}>
        <div className={classes.leftContainer}>
          {showRevampSearchBox ? (
            <SearchBoxComponent
              options={options}
              handleSearchClick={handleSearchClick}
              loading={loading}
              searchNow={searchNow}
              setSearchNow={setSearchNow}
              showCrossMark={showCrossMark}
              setShowCrossMark={setShowCrossMark}
              inputValue={inputValue}
              setInputValue={setInputValue}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          ) : (
            <></>
          )}
        </div>
        <div className={classes.rightContainer}>
          <div>
            <CloudAccountChangeV2
              cloudAccounts={filteredCloudAccounts}
              selectedAccountId={selectedAccountIdWithCloud}
              onChange={handleCloudAccountChange}
              width={220}
              size="small"
            />
          </div>
          <div className={classes.dropDownWidth}>
            <ProductSelectorDropdown
              functionType={FlyoutFunction.SUBSCRIPTION}
            />
          </div>
          {selectedCloudTypeProductListing === 'AWS' && createButton}
          {['AWS', 'AZURE', 'GCP'].includes(
            selectedCloudTypeProductListing
          ) && (
            <Filters
              sections={sections}
              applyFilters={applyFilters}
              minWidth="575px"
            />
          )}
        </div>
      </div>
      {selectedFilter && !!selectedFilter?.length && (
        <div
          className={clsx(
            classes.filtersAppliedContainer,
            selectedFilter?.length ? classes.selectedFilter : ''
          )}
          data-testid="filters-applied-component"
        >
          <FiltersApplied
            selectedValues={selectedFilter}
            onDelete={onDeleteFiltersAppliedChip}
            chipDisplayLimit={5}
            noBorder
          />
        </div>
      )}
      {isModalOpen && (
        <SubscriptionSelectionModal
          open={isModalOpen}
          handleModalClose={handleModalClose}
          acceptSelectedSubscription={acceptSelectedSubscription}
          currentSelectedSubscriptionId={selectedSubscription}
          acceptLoading={loadingActiveResellerAuth}
          type={'MODAL'}
        />
      )}
      <ChannelOfferCreationModal
        open={cppoSubscription}
        setOpen={setCppoSubscription}
        handleNext={handleNext}
        subscriptionId={selectedSubscription}
      />
    </>
  )
}
